<template>
  <l-map
    v-bind="$attrs"
    ref="lMapRef"
    :zoom="zoom"
    :center="center"
    :options="{
      zoomSnap: 0.5,
      doubleClickZoom: false,
      markerZoomAnimation: false,
      zoomAnimation: false,
    }"
    data-testid="map"
    style="height: 100%; width: 100%; min-height: 150px; min-width: 150px"
    @zoomend="emitZoomLevelChanged"
    @ready="onMapReady"
  >
    <l-tile-layer
      :url="url"
      :attribution="attribution"
    />
    <slot name="map" />
    <map-marker
      v-if="
        locationSearchResult && locationSearchResult.geometry.type === 'Point'
      "
      :key="locationSearchResult.locationId"
      :interactive="false"
      :lat-lng="locationSearchResult.geometry.coordinates"
      icon="red"
      animation="bounce"
    />
    <l-polygon
      v-else-if="
        locationSearchResult && locationSearchResult.geometry.type === 'Polygon'
      "
      color="#e55244"
      fill-color="#e55244"
      :lat-lngs="locationSearchResult.geometry.coordinates"
    />
    <l-polygon
      v-for="(polygon, i) in locationSearchResult.geometry.coordinates"
      v-else-if="
        locationSearchResult &&
          locationSearchResult.geometry.type === 'MultiPolygon'
      "
      :key="i"
      color="#e55244"
      fill-color="#e55244"
      :lat-lngs="polygon"
    />
    <l-polygon
      :lat-lngs="austriaPolygonData"
      color="#6F7189"
      :weight="2"
      :fill="false"
      :interactive="false"
    />
    <l-polygon
      :lat-lngs="federalStatesPolygonData"
      color="#6F7189"
      :weight="1"
      :fill="false"
      :interactive="false"
    />
    <div
      @click="stopClick"
      @mouseenter="disableDragging"
      @mouseleave="enableDragging"
    >
      <div class="floating-div mr-4 mt-4">
        <div class="toolbar d-flex flex-wrap justify-end ml-12">
          <slot name="tools" />
          <div>
            <map-search-bar
              class="width-300"
              @zoom-to-location="zoomToLocation"
              @reset-search="resetSearchState"
            />
          </div>
        </div>
      </div>
    </div>
  </l-map>
</template>

<script lang="ts">
import { defineComponent, ref, provide, watch, onBeforeUnmount } from 'vue';
import L, { FitBoundsOptions, latLng as toLatLng, LatLng, LatLngBounds } from 'leaflet';
import { LMap, LTileLayer, LPolygon } from '@vue-leaflet/vue-leaflet';
import MapSearchBar, {
  GeoJsonGeom,
  ZoomProps,
} from '@/components/common/map/MapSearchBar.vue';
import MapMarker from '@/components/common/map/Marker.vue';
import austriaPolygon from '@/assets/polygons/austriaPolygon';
import federalStatesPolygon from '@/assets/polygons/federalStatesPolygon';
import GridStore from '@/stores/grid/grid-module';

interface LocationSearchResult {
  geometry: GeoJsonGeom;
  locationId: number;
}

interface LMapWithMapObject extends InstanceType<typeof LMap> {
  mapObject: L.Map
}

export default defineComponent({
  name: 'SharedMap',
  components: {
    MapSearchBar,
    LMap,
    LTileLayer,
    LPolygon,
    MapMarker,
  },
  props: {
    zoom: { type: Number, default: 7.5 },
    center: {
      type: Object as () => LatLng,
      default: () => toLatLng(47.768868, 13.535),
    },
    drawingToolCursor: {
      type: String as () => 'crosshair' | 'none' | '',
      default: ''
    }
  },
  emits: ['l-map-ready', 'zoomLevelChanged'],
  setup(props, { emit }) {
    const lMapRef = ref<LMapWithMapObject | null>(null);
    const leafletMap = ref<L.Map | null>(null);

    const url = 'https://tile.openstreetmap.de/{z}/{x}/{y}.png';
    const attribution =
      '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>';

    const austriaPolygonData = austriaPolygon;
    const federalStatesPolygonData = federalStatesPolygon;
    const gridStore = GridStore;

    const locationSearchResult = ref<LocationSearchResult | null>(null);

    const resizeObserver = ref<ResizeObserver | null>(null);

    function emitZoomLevelChanged() {
      if (!leafletMap.value) return
      emit('zoomLevelChanged', leafletMap.value.getZoom())
    }

    function onMapReady(map: L.Map) {
      leafletMap.value = map;
      emit('l-map-ready', map);

      const mapContainer = lMapRef.value?.$el as HTMLElement;
      if (mapContainer) {
        resizeObserver.value = new ResizeObserver(() => {
          leafletMap.value?.invalidateSize();
        });
        resizeObserver.value.observe(mapContainer);
      }
    }

    function zoomToLocation(zoomProps: ZoomProps, options?: FitBoundsOptions) {
      if (!leafletMap.value) return;
      leafletMap.value.fitBounds(zoomProps.latlng, {
        animate: false,
        ...options,
      });
      locationSearchResult.value = {
        geometry: zoomProps.geojson,
        locationId: zoomProps.id,
      };
    }

    function zoomToBbox(bbox: LatLngBounds) {
      if (!leafletMap.value) return;
      leafletMap.value.fitBounds(bbox);
    }

    function resetSearchState() {
      if (!leafletMap.value) return;
      locationSearchResult.value = null;
      leafletMap.value.setView(props.center, props.zoom);
    }

    function resetView() {
      if (!leafletMap.value) return;
      leafletMap.value.setView(props.center, props.zoom);
    }

    function stopClick(e: MouseEvent) {
      e.stopPropagation()
    }

    function disableDragging() {
      if (!leafletMap.value) return;
      leafletMap.value.dragging.disable();
    }

    function enableDragging() {
      if (!leafletMap.value) return;
      leafletMap.value.dragging.enable()
    }

    watch(
      () => gridStore.leafletBbox,
      (newBbox) => {
        if (!leafletMap.value || props.drawingToolCursor) return;
        if (newBbox) {
          zoomToBbox(newBbox);
        } else {
          resetView();
        }
      }
    );

    watch(
      () => props.drawingToolCursor,
      (cursorState) => {
        if (!leafletMap.value) return;
        const container = leafletMap.value.getContainer();
        container.style.cursor = cursorState;
      },
      { immediate: true }
    );

    onBeforeUnmount(() => {
      resizeObserver.value?.disconnect();
    });

    // provide the actual Leaflet map instance to descendants
    provide('leafletMap', leafletMap);

    return {
      lMapRef,
      url,
      attribution,
      austriaPolygonData,
      federalStatesPolygonData,
      locationSearchResult,
      zoomToLocation,
      resetSearchState,
      emitZoomLevelChanged,
      onMapReady,
      stopClick,
      disableDragging,
      enableDragging,
    };
  },
});
</script>

<style scoped>
.floating-div {
  position: absolute;
  z-index: 999;
  top: 0;
  right: 0;
}

:deep(.toolbar) > * {
  /* margin-bottom for all children of toolbar; when there is a linebreak there should be vertical space between them*/
  margin-bottom: 8px;
}

:deep(.v-text-field .v-input__control) {
  /* set min-height to auto because otherwise there is an unnecessarily high div around the text fields*/
  min-height: auto !important;
}

.width-300 {
  width: 300px;
}
</style>
